import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { COLOR_BY_LEVEL, Level } from '../utils/utils';

export const Legend = () => {
  const [selected, setSelected] = useState(false);
  return (
    <Tooltip
      arrow
      open={selected}
      title={
        <ProgressPortraitInterpretation
          clickAwayFunction={() => setSelected(false)}
        />
      }
      placement='bottom-start'
      PopperProps={{
        disablePortal: true,
        sx: {
          '& .MuiTooltip-tooltip': {
            width: 1000,
            maxWidth: 'min(1200px, 100vw)',
            borderRadius: 2,
            // overflowX: 'auto',
            p: 0,
            scrollbarWidth: 0,
          },
          '& .MuiTooltip-tooltip::-webkit-scrollbar': {
            display: 'none',
          },
          '& .MuiTooltip-tooltip,.MuiTooltip-arrow:before': {
            backgroundColor: '#fff',
            boxShadow: 3,
          },
          '&.MuiTooltip-popper': {
            zIndex: 15,
          },
        },
      }}
    >
      <Button
        variant={selected ? 'contained' : 'outlined'}
        onClick={() => setSelected(!selected)}
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          py: 2,
          borderRadius: 2,
        }}
      >
        <FormatListBulletedIcon sx={{ fontSize: 20 }} />
        <Typography fontSize={14} fontWeight={500}>
          Legend
        </Typography>
      </Button>
    </Tooltip>
  );
};

const data = [
  {
    label: 'Goal Progress',
    metrics: [
      {
        title: '% of Goals Mastered',
        options: [
          { text: 'Below 50%', level: 'low' },
          { text: '50 - 75%', level: 'medium' },
          { text: 'Above 75%', level: 'high' },
        ],
      },
    ],
  },
  {
    label: 'Developmental Milestones',
    metrics: [
      {
        title: 'Vineland-3 Adaptive Behavior Composite',
        options: [
          { text: 'Low', level: 'low' },
          { text: 'Moderately low', level: 'medium' },
          { text: 'Adequate or above', level: 'high' },
        ],
      },
    ],
  },
  {
    label: 'Problem Behavior',
    metrics: [
      {
        title: 'Vineland-3 Maladaptive Behavior Index',
        options: [
          { text: 'Clinically Significant', level: 'low' },
          { text: 'Elevated', level: 'medium' },
          { text: 'Average', level: 'high' },
        ],
      },
    ],
  },
  {
    label: 'Dangerous Behavior',
    metrics: [
      {
        title: 'Dangerous behaviors exhibited?',
        options: [
          { text: 'Yes', level: 'low' },
          { text: null, level: 'NA' },
          { text: 'No', level: 'high' },
        ],
      },
    ],
  },
  {
    label: 'Parent Stress',
    metrics: [
      {
        title: 'Parent Stress Scale (PSS)',
        options: [
          { text: 'High', level: 'low' },
          { text: 'Moderately High', level: 'medium' },
          { text: 'Average', level: 'high' },
        ],
      },
      {
        title: 'Parent Stress Index (PSI)',
        options: [
          { text: 'Clinically Significant', level: 'low' },
          { text: 'High', level: 'medium' },
          { text: 'Normal', level: 'high' },
        ],
      },
      {
        title: 'Stress Index for Parents of Adolescents (SIPA)',
        options: [
          { text: 'Clinically Significant or above', level: 'low' },
          { text: 'Borderline', level: 'medium' },
          { text: 'Within normal limits', level: 'high' },
        ],
      },
    ],
  },
];

const ProgressPortraitInterpretation = ({
  clickAwayFunction,
}: {
  clickAwayFunction: VoidFunction;
}) => {
  return (
    <ClickAwayListener onClickAway={clickAwayFunction}>
      <Box sx={{ p: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Typography fontWeight={500} color='text.primary'>
          Interpretation
        </Typography>
        {data.map((item, index) => (
          <Box
            key={item.label}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            <Typography fontSize={14} fontWeight={500} color='text.primary'>
              {item.label}
            </Typography>
            {item.metrics.map((metric) => (
              <Box
                key={metric.title}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                }}
              >
                <Box sx={{ flex: '0 0 350px' }}>
                  <Typography fontSize={12} color='#252838'>
                    {metric.title}
                  </Typography>
                </Box>
                {metric.options.map(({ text, level }, index) =>
                  text == null ? (
                    <Box key={index} sx={{ flex: '0 0 200px' }}></Box>
                  ) : (
                    <Box
                      key={index}
                      sx={{
                        flex: '0 0 200px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: COLOR_BY_LEVEL[level as Level],
                        }}
                      ></Box>
                      <Typography fontSize={12} color='#252838'>
                        {text}
                      </Typography>
                    </Box>
                  ),
                )}
              </Box>
            ))}
            {data.length - 1 !== index && <Divider color='#DCDAE2' />}
          </Box>
        ))}
      </Box>
    </ClickAwayListener>
  );
};
